import ByLicense from './License';
import ByModel from './Model';
import BySize from './Size';
import Tabs from '../tabs';
import Mustache from 'mustache';
import { init as initGoTo } from '../goto';
import { toggle } from '@nt-workspace/common';
import notification from '../notification';

declare global {
  interface Window {
    loc: {
      tyreselector_no_size_data: string;
      tyreselector_try_again_later: string;
      tyreselector_range_inches: string;
      tyreselector_factory_default: string;
      tyreselector_no_data: string;
    }
  }
}

export type Size = [
  number, // width (mm)
  number, // height (aspect ratio)
  number, // rim (inch)
  number, // load index
  string, // Speed index
];

export type FilterGroup = {
  id: string;
  product_page: string;
  slug: string;
};

export type TsType = 'embed' | 'default';
export type TsVersion = 'fitment'; // All countries use fitment version

// Data passed from server to client, to configure tyreselector
export type TsSettings = {
  type: TsType;
  version: TsVersion;
  selectedLang: string;
  tyresPage: string;
  sizeData: Size[];
  activeSeason: string;
  byTyreSizeCategorySlug: string;
  filterGroups: FilterGroup[];
  speedRatings: string[];
  filterGroupSlug?: string; // Override current season on tyres page, since we want to redirect to same season
  loc: {
    [key: string]: string;
  };
  initialTab: string;
}

// Data saved to session storage for other js to use
export type TyreData = {
  height: number;
  width: number;
  rim: number;
  manufacturer: {
    value: string;
    text: string;
  };
  model: {
    value: string;
    text: string;
  };
  vehicle: {
    value: string;
    text: string;
  };
  year: {
    value: string;
    text: string;
  };
  license: {
    country: string;
    data: {
      center_bore: number;
      from: number;
      hp: number;
      id: string;
      kw: number;
      manufacturer: string;
      model: string;
      pcd: string;
      to: number | null;
      year: number;
      tyres: {
        /** Key is tyre size string, e.g. '215/55R17'. Boolean is manufacturer's default size. */
        [key: string]: boolean;
      };
      wheels: {
        /** Key is tyre size string, e.g. '215/55R17'. */
        [key: string]: {
          /** Key is wheel size string, e.g. '6.5x17/null'. Boolean is manufacturer's default size? */
          [key: string]: boolean;
        }
      };
    };
    text: string;
  },
  rimSizeRange?: string;
};

type AnalyticsData = {
  tyre_search_type: string;
  tyre_size: string;
  vehicle_manufacturer?: string;
  vehicle_year?: string;
  vehicle_model?: string;
  vehicle_engine?: string;
}

type RedirectOptions = {
  analyticsEvent?: AnalyticsData;
  extraParams?: {
    [key: string]: string;
  };
  hash?: string;
}

export default class TyreSelector {
  dom: {
    root: HTMLElement,
    header: HTMLElement,
    tabs: HTMLElement[],
    tablist: HTMLElement,
    filterbarSizes: HTMLElement,
    footer: HTMLElement,
    jsToggle: HTMLElement, // js-toggle-element in header to toggle tyre selector view
  };

  tablist!: Tabs;

  modules!: {
    size: BySize,
    license: ByLicense,
    model: ByModel,
  };

  // Store last active tab to session storage
  #storageKey = 'ts__view';
  // Store visibility state
  #visibilityStateKey = 'ts__visibility';

  // Data for all tyre sizes
  sizeData: Size[] = [];

  // Settings
  type: TsType;
  version: TsVersion;
  selectedLang: string;
  tyresPage: string;
  activeSeason: string;
  byTyreSizeCategorySlug: string;
  filterGroups: FilterGroup[] = [];
  speedRatings: string[] = [];
  filterGroupSlug?: string;
  loc: {
    [key: string]: string;
  };
  initialTab: string;

  /**
   * Tyre selector
   * *
   * This contains the tabbing functionality between views
   * as well as common methods that modules can use.
   *
   * @param {HTMLElement} element - Root element of the tyre selector
   */
  constructor(element: HTMLElement) {
    // Define dom elements
    this.dom = {
      root: element,
      tabs: Array.from(element.querySelectorAll('[role="tabpanel"]')),
      tablist: element.querySelector('[role="tablist"]') as HTMLElement,
      filterbarSizes: document.getElementById('ts-filter-bar-sizes') as HTMLElement,
      header: element.querySelector('.ts__title') as HTMLElement,
      jsToggle: element.querySelector('[data-toggle-target=ts-content]') as HTMLElement,
      footer: document.querySelector('.ts__footer') as HTMLElement,
    };

    // Define settings
    let settings = {} as TsSettings;
    try {
      settings = element.dataset.tsSettings ? JSON.parse(element.dataset.tsSettings) : {};
      console.log('TyreSelector.constructor()', settings);
    } catch (e) {
      console.error('[TyreSelector]: could not parse settings', e);
    }

    if (['embed', 'default'].includes(settings.type)) {
      this.type = settings.type;
    } else {
      throw new Error(`[TyreSelector]: Invalid type "${settings.type}"`);
    }

    if (['fitment'].includes(settings.version)) {
      this.version = settings.version;
    } else {
      throw new Error(`[TyreSelector]: Invalid version "${settings.version}"`);
    }

    if (settings.sizeData && settings.sizeData.length) {
      this.sizeData = settings.sizeData;
    }

    if (settings.filterGroups && settings.filterGroups.length) {
      this.filterGroups = settings.filterGroups;
    }

    if (settings.speedRatings && settings.speedRatings.length) {
      this.speedRatings = settings.speedRatings;
    }

    this.selectedLang = settings.selectedLang || document.documentElement.lang || 'en';
    this.tyresPage = settings.tyresPage || '';
    this.activeSeason = settings.activeSeason || '';
    this.byTyreSizeCategorySlug = settings.byTyreSizeCategorySlug || '';
    this.filterGroupSlug = settings.filterGroupSlug || '';
    this.loc = settings.loc || {};
    this.initialTab = settings.initialTab || 'size';

    const dataOk = this.type && this.version && this.sizeData.length && this.filterGroups.length && this.speedRatings.length && this.selectedLang && this.tyresPage && this.activeSeason && this.byTyreSizeCategorySlug;

    // Set visibility. Never hide embed version
    const hidden = sessionStorage.getItem(this.#visibilityStateKey) === 'hidden';
    if (hidden && this.type !== 'embed') {
      // Set correct toggle values
      toggle.toggle(this.dom.jsToggle, false);

      this.dom.tablist.classList.add('hidden');
    }

    if (dataOk && this.dom.root && this.dom.tabs.length && this.dom.tablist) {
      console.log('TyreSelector.init()');

      // Load modules per tab
      this.modules = {} as any;
      this.dom.tabs.forEach(tabEl => {
        if (tabEl.dataset.type === 'size') {
          this.modules.size = new BySize(tabEl, this);
        }

        if (tabEl.dataset.type === 'license') {
          this.modules.license = new ByLicense(tabEl, this);
        }

        if (tabEl.dataset.type === 'model') {
          this.modules.model = new ByModel(tabEl, this);
        }
      });

      const activeTabType = sessionStorage.getItem(this.#storageKey);
      const activeTab = this.dom.root.querySelector(`[role="tab"][data-type="${activeTabType}"]`)?.id;
      if (activeTab) {
        this.initialTab = activeTab;
      }

      // Initialise tabbed interface
      this.#initTabs(this.initialTab);

      this.initByModelFooter();

      //this.#sendAnalyticsEvent();

    } else {
      console.error('[TyreSelector]: could not init. Missing data or dom elements.');
    }
  }

  /**
   * Initialise tabbed interface functionality and
   * sets last viewed tab from session storage as active
   * Note: this sets the same value to all tyreselectors on page
   */
  #initTabs(activeTabId) {
    this.tablist = new Tabs(this.dom.tablist);
    //this.dom.tablist.addEventListener('tabs.change', this.onTabChange.bind(this) as EventListener);


    this.tablist.showTab(activeTabId);
    this.updateTSHeader(activeTabId);

  }

  /**
   * Filter bar sizes come from tyredata and backend has no access to that. Initialize sizes
   * Only if element exists
   */
  initFilterbarSizes() {
    if (this.dom.filterbarSizes) {
      try {
        const tyreData = JSON.parse(sessionStorage.getItem('tyreData') || '[]');
        const sizesTemplate = document.getElementById('ts-header_filter_bar_sizes_template') as HTMLTemplateElement;
        const url = this.getSizePageUrl();

        if (url.includes('false') || url.includes('null')) {
          console.error(`[TyreSelector.initFilterbarSizes()]: Invalid URL "${url}"`);
          return;
        }

        const sizes = (tyreData?.availableSizes || []).map(s => ({
          ...s,
          selected: s.width === tyreData.width && s.height === tyreData.height && s.rim === tyreData.rim,
          path: url,
        }));

        if (sizes.length > 0 && sizesTemplate) {
          this.dom.filterbarSizes.innerHTML = Mustache.render(sizesTemplate.innerHTML, { sizes });
          this.dom.filterbarSizes.querySelectorAll('select').forEach(select => {
            initGoTo(select);
          });

          const container = this.dom.filterbarSizes?.closest('#ts-filter-bar-sizes-container');

          if (container) {
            container.classList.remove('hidden');
          }
        }
      } catch (e) {
        console.error('[initFilterbarSizes]: could not parse tyreData', e);
      }
    }
  }

  addTsTargetEventListeners() {
    // Add event listeners to buttons
    const tsTargets = document.querySelectorAll('[data-ts-target]');
    tsTargets.forEach(tsTarget => {
      tsTarget.addEventListener('click', (event) => {
        this.tablist.showTab('model');
        this.modules.model.onNotificationClick((tsTarget as HTMLElement).dataset.tsTarget);
      });
    })
  }


  /**
   * TS footer is shown in model view. It contains sizes dropdown and rim size range info
   */
  initByModelFooter() {

    const footerTemplate = document.getElementById('ts-footer_template')?.textContent || '';

    try {
      const tyreData = JSON.parse(sessionStorage.getItem('tyreData') || '[]');
      if (this.initialTab === 'model' && this.dom.footer && footerTemplate && tyreData && !tyreData?.vehicle?.value) {

        // Create sizes
        const sizes = (tyreData.availableSizes || []).map(s => ({
          ...s,
          selected: s.width == tyreData.width && s.height == tyreData.height && s.rim == tyreData.rim,
        }));

        const footerData = {
          sizes,
          hasSizes: sizes.length > 0,
          rimSizeRange: false,
          showCTA: true // Show the CTA if engine isn't selected
        }

        // Add info about rim size range
        if (tyreData.rimSizeRange) {
          footerData.rimSizeRange = tyreData['rimSizeRange'];
        }

        // Render footer
        this.dom.footer.innerHTML = Mustache.render(footerTemplate, footerData);

        this.addTsTargetEventListeners();

        // Add event listener to select
        // Selecting a size should direct user to size view with the selected size
        const sizeSelect = this.dom.footer.querySelector('.ts-by-model-sizes');
        if (sizeSelect) {
          sizeSelect.addEventListener('change', (event) => {
            this.setLoading(true);
            const tyreSize = (event.target as HTMLSelectElement).value.split('-');
            this.storeTyreData({
              ...tyreData,
              width: tyreSize[0],
              height: tyreSize[1],
              rim: tyreSize[2]
            }, 'size');

            this.setView('size');
            this.modules.size.setSessionData({
              width: parseInt(tyreSize[0]),
              height: parseInt(tyreSize[1]),
              rim: parseInt(tyreSize[2]),
            });

            this.redirectToSizePage(tyreSize[0], tyreSize[1], tyreSize[2]);
          });
        }
      } else {
        this.addTsTargetEventListeners();
      }
    } catch (e) {
      console.error('[initByModelFooter]: could not parse tyreData', e);
    }
  }

  /**
   * Save active tab to session storage
   */
  onTabChange(event: CustomEvent) {
    // Tab id is button id
    if (event.detail.tabId) {
      const tabType = (this.dom.root.querySelector(`#${event.detail.tabId}`) as HTMLElement)?.dataset.type;
      if (tabType) {
        sessionStorage.setItem(this.#storageKey, tabType);
      }
    }
  }

  /**
   * Sets loading state for entire tyre selector
   */
  setLoading(state: boolean) {
    this.dom.root.classList.toggle('loading', state);
  }

  /**
   * Set visibility state
   * Tyreselector is hidden if selections are already made
   */
  setVisibility(state: string) {
    sessionStorage.setItem(this.#visibilityStateKey, state);
  }

  /** Show TS */
  show() {
    toggle.toggle(this.dom.jsToggle, true);
  }

  /**
   * Update tyre selector header by view
   * @param view - View name
   * Not applicable to embedded version
   */
  updateTSHeader(view: string) {
    const template = window['ts-header_' + view + '_template']?.textContent || null;
    try {
      const tyreData = JSON.parse(sessionStorage.getItem('tyreData') || '{}');

      if (template && this.dom.header && Object.keys(tyreData).length > 0 && this.type !== 'embed') {
        this.dom.header.innerHTML = Mustache.render(template, { ...tyreData });
      }
    } catch (e) {
      console.error('[TyreSelector.updateTSHeader()]: could not parse tyreData', e);
    }
  }

  /**
   * Legacy support.
   * This equals window.setTyreCookie().
   * Other functionality (like dealers, tire-sizes etc) read 'tyreData' from session storage
   * So store tyreSelector data in session storage
   */
  storeTyreData(data: Partial<TyreData>, module: string) {
    if (typeof data !== 'object' || data === null) {
      return;
    }
    // Deep copy
    const dataToSave = JSON.parse(JSON.stringify(data));

    // Remove other data if manufacturer is not set
    if (!dataToSave?.manufacturer?.value) {
      delete dataToSave.year;
      delete dataToSave.model;
      delete dataToSave.vehicle;
    }

    sessionStorage.setItem('tyreData', JSON.stringify(dataToSave));
  }

  /**
   * Get size page url
   * Used for filterbar and redirecting to results
   */
  getSizePageUrl() {
    const tyresPage = this.tyresPage || false;
    const byTyreSizeCategorySlug = this.byTyreSizeCategorySlug || false;
    const season = this.getSeason();
    const filterGroupSlug = this.filterGroupSlug || this.filterGroups.find(group => group.id === season)?.slug || false;
    const url = `${tyresPage}${byTyreSizeCategorySlug}/${filterGroupSlug}`;

    return url;
  }

  /**
   * Redirects to size page.
  * @param {number} width - Tyre width
  * @param {number} height - Tyre height
  * @param {number} rim - Tyre rim
  * @param {object} options - Other options
  */
  redirectToSizePage(width, height, rim, options: RedirectOptions = {}) {
    const { analyticsEvent, extraParams, hash } = options;
    // create extra params.
    // existing values = noEngine=true
    const params = new URLSearchParams(extraParams).toString();
    const url = `${this.getSizePageUrl()}/${width}-${height}-${rim}/${params.length ? `?${params}` : ''}${hash ? `#${hash}` : ''}`;

    if (url.includes('false') || url.includes('null')) {
      console.error(`[TyreSelector.redirectToSizePage()]: Invalid URL "${url}"`);
    } else {
      if (analyticsEvent) {
        this.#sendAnalyticsEvent(analyticsEvent);
      }
      setTimeout(() => {
        window.location.href = url;
      }, 500);
    }
  }

  /**
   * Set view
   */
  setView(view) {
    sessionStorage.setItem(this.#storageKey, view);
  }

  /**
   * Get current season
   * This method determines what season should be used with current user.
   * 1) Check if GeoIP rules have chosen the season, use it if available
   * 2) Use site's active season
   *
   * @return {(string|boolean)} season - false if could not choose season, otherwise any of the filter group id's summer/winter/studded/non-studded...
   */
  getSeason() {
    let season: string | boolean = false;
    // First check if geo_season has selected a season for current user. This feature is in use @ US/CA sites.
    const geoSeason = sessionStorage.getItem('geo_season');
    if (geoSeason) {
      season = geoSeason.split('_').join('-');
    }
    // if no geoSeason, then use site's active season.
    if (!season && this.activeSeason) {
      season = this.activeSeason;
    }

    if (!season) {
      // Ideally should never end up here
      console.error('[TyreSelector.getSeason()]: could not choose season');
    }

    return season;
  }

  /**
   * Set analytics event to storage
   */
  setAnalyticsEvent(data: AnalyticsData) {
    sessionStorage.setItem('tyreAnalyticsEvent', JSON.stringify(data));
  }
  /**
   * Send analytics event if it exists
   * Event is stored in session storage right after search is performed
   *
  */
  #sendAnalyticsEvent(analyticsEvent) {
    try {
      analyticsEvent = analyticsEvent || JSON.parse(sessionStorage.getItem('tyreAnalyticsEvent') || '{}');

      // search type and size need to be sent always
      if (analyticsEvent && analyticsEvent.tyre_search_type && analyticsEvent.tyre_size) {
        analyticsEvent.event = 'tyre_search_submit';

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(analyticsEvent);

        // remove event from storage so we don't send it again
        sessionStorage.removeItem('tyreAnalyticsEvent');
      }
    } catch (e) { }
  }

}

export function init(elements: NodeListOf<HTMLElement>) {
  for (let element of elements) {
    new TyreSelector(element);
  }
}
